import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  flowRight,
  isNil,
} from 'lodash';
import { ELSButton } from '@els/els-component-button-react';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import { getObjectPropsArray } from '../../utilities/common.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import { Brand } from '../../constants/common.constants';
import {
  WelcomeDisclaimerConfigMap
} from '../../components/welcome-disclaimer-modal/welcome-disclaimer.config';
import {
  fetchLatestAcceptancePerBrand,
  resetAcceptance,
  WelcomeDisclaimerRecord
} from '../../components/welcome-disclaimer-modal/welcome-disclaimer.utilities';

export const SupportComponent = (): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef,
  } = useContext(AppContext);

  const [disclaimerRecords, setDisclaimerRecords] = useState<Partial<Record<Brand, WelcomeDisclaimerRecord>>>({});
  const [isLoadingDisclaimers, setIsLoadingDisclaimers] = useState<boolean>(false);
  const [resettingBrand, setResettingBrand] = useState<Brand | null>(null);

  const userId = appState.eolsUser?.id;

  const loadDisclaimerRecords = () => {
    if (!userId) {
      return;
    }
    setIsLoadingDisclaimers(true);
    fetchLatestAcceptancePerBrand({
      appState,
      setAppState,
      appStateRef,
      userId,
    })
      .then((records) => {
        setDisclaimerRecords(records);
      })
      .catch(() => {
        setDisclaimerRecords({});
      })
      .finally(() => {
        setIsLoadingDisclaimers(false);
      });
  };

  useEffect(() => {
    loadDisclaimerRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleReset = (brand: Brand) => {
    if (!userId) {
      return;
    }
    setResettingBrand(brand);
    resetAcceptance({
      appState,
      setAppState,
      appStateRef,
      userId,
      brand,
    })
      .then(() => {
        // Full reload so the registrar's in-memory "accepted this session"
        // guard resets — otherwise navigating back to /ai-chat would
        // short-circuit and the modal wouldn't re-trigger.
        window.location.reload();
      })
      .finally(() => {
        setResettingBrand(null);
      });
  };

  const getCourseDataRows = () => {
    if (!appState.currentCourse) {
      return [];
    }

    return getObjectPropsArray(appState.currentCourse, [], '');
  };

  const getUserDataRows = () => {
    if (!appState.eolsUser) {
      return [];
    }

    return getObjectPropsArray(appState.eolsUser, [], '');
  };

  const renderRow = (row: { prop: string; val: unknown }): JSX.Element => {
    return (
      <tr key={row.prop}
        className="c-els-table__row">
        <td className="c-els-table__cell">
          {row.prop}
        </td>
        <td className="c-els-table__cell">
          {isNil(row.val) ? null : row.val.toString()}
        </td>
      </tr>
    );
  };

  const renderDisclaimerRow = (brand: Brand): JSX.Element => {
    const config = WelcomeDisclaimerConfigMap[brand];
    const record = disclaimerRecords[brand];
    return (
      <tr key={brand} className="c-els-table__row">
        <td className="c-els-table__cell">{config.productName}</td>
        <td className="c-els-table__cell">{record ? record.stateInfo.version : '—'}</td>
        <td className="c-els-table__cell">{config.version}</td>
        <td className="c-els-table__cell">{record ? record.stateInfo.acceptedAt : '—'}</td>
        <td className="c-els-table__cell">{record ? record.historyId : '—'}</td>
        <td className="c-els-table__cell">
          <ELSButton
            type="secondary"
            preventAutoPending
            isDisabled={!userId || resettingBrand === brand}
            onClick={() => handleReset(brand)}
          >
            {resettingBrand === brand ? 'Resetting…' : 'Reset'}
          </ELSButton>
        </td>
      </tr>
    );
  };

  const buildVersion = window.getBuildVersion ? window.getBuildVersion() : null;

  return (
    <>
      <h1>Support</h1>

      <div className="o-els-container">
        <span>{`App Version: ${buildVersion}`}</span>
      </div>

      <div className="o-els-container">
        <h3>Course info</h3>
        <table className="c-els-table c-els-table--padding-1o2 c-els-table--vertical-dividers">
          <tbody className="c-els-table__body">
            {
              getCourseDataRows().map((row) => {
                return renderRow(row);
              })
            }
          </tbody>
        </table>
      </div>

      <div className="o-els-container">
        <h3>User info</h3>
        <table className="c-els-table c-els-table--padding-1o2 c-els-table--vertical-dividers">
          <tbody className="c-els-table__body">
            {
              getUserDataRows().map((row) => {
                return renderRow(row);
              })
            }
          </tbody>
        </table>
      </div>

      <div className="o-els-container">
        <h3>Welcome disclaimer acknowledgement</h3>
        {!userId && <p>Sign in to view acknowledgement records.</p>}
        {userId && isLoadingDisclaimers && <p>Loading…</p>}
        {userId && !isLoadingDisclaimers && (
          <table className="c-els-table c-els-table--padding-1o2 c-els-table--vertical-dividers">
            <thead>
              <tr className="c-els-table__row">
                <th className="c-els-table__cell c-els-table__cell--head">Brand</th>
                <th className="c-els-table__cell c-els-table__cell--head">Accepted version</th>
                <th className="c-els-table__cell c-els-table__cell--head">Current version</th>
                <th className="c-els-table__cell c-els-table__cell--head">Accepted at</th>
                <th className="c-els-table__cell c-els-table__cell--head">History id</th>
                <th className="c-els-table__cell c-els-table__cell--head">Actions</th>
              </tr>
            </thead>
            <tbody className="c-els-table__body">
              {Object.values(Brand).map(renderDisclaimerRow)}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Support' }),
  withPageLoader,
);

const Support = enhance(SupportComponent);
export default Support;
